import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AxiosConfig from '../../../config/AxiosConfig';
import parseSessionStorageVariable from '../../helpers/utils/parseSessionStorage';
import Filters from './Filters.jsx';
import TicketList from './TicketList.jsx';

const UserStatsRowLazyLoading = ({ user }) => (
  <TableRow>
    <TableCell>
      {user?.isActive ? (
        <CheckCircleIcon size="small" color="success" />
      ) : (
        <CancelIcon size="small" color="error" />
      )}
    </TableCell>
    <TableCell>
      <Skeleton variant="text" animation="wave" />
    </TableCell>
    <TableCell align="right">
      <Skeleton variant="text" animation="wave" />
    </TableCell>
    <TableCell align="right">
      <Skeleton variant="text" animation="wave" />
    </TableCell>
    <TableCell align="right">
      <Skeleton variant="text" animation="wave" />
    </TableCell>
    <TableCell align="right">
      <Skeleton variant="text" animation="wave" />
    </TableCell>
    <TableCell align="right" sx={{ lineHeight: 2.5 }}>
      <Skeleton variant="text" animation="wave" />
    </TableCell>
  </TableRow>
);

const RolesChip = ({ role }) => {
  const getChipProps = useCallback(() => {
    if (role?.name?.includes('Operateur SCO'))
      return { color: 'primary', label: role?.name };
    if (role?.name?.includes('Operateur SAV'))
      return { color: 'success', label: role?.name };
    if (role?.name?.includes('Operateur SLO'))
      return { color: 'info', label: role?.name };
    if (role?.name?.includes('Responsable'))
      return { color: 'error', label: role?.name };
    if (role?.name?.includes('Administrateur'))
      return { color: 'warning', label: role?.name };
    return { label: role?.name };
  }, [role?.name]);

  const chipProps = getChipProps();

  return (
    <>
      <Chip variant="outlined" {...chipProps} />
      <br />
    </>
  );
};

const UserStatsRow = ({ user, refetchUserStats }) => {
  const profile = useSelector((state) => state?.login?.profile);
  const [open, setOpen] = useState(
    parseSessionStorageVariable('stats_assigned_ticket_is_open') === user?._id
  );
  const [button, setButton] = useState(
    parseSessionStorageVariable('stats_assigned_ticket_status') || ''
  );
  const [tickets, setTickets] = useState(null);
  const [page, setPage] = useState(
    parseSessionStorageVariable('stats_assigned_ticket_page') || 0
  );
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const visibleRows = useMemo(() => {
    return (
      tickets?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) ||
      null
    );
  }, [tickets, page, rowsPerPage]);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const dataCount = user?.stats || {
    nbNewTickets: 0,
    nbInProgressTickets: 0,
    nbWaitingTickets: 0,
    nbProcessedTickets: 0,
  };

  const handleDetailsLoad = useCallback(
    async (status) => {
      setIsLoadingDetails(true);
      try {
        await AxiosConfig.create('post', `/stats/${user?._id}`)
          .call({ status: status, details: true })
          .exit((res) => {
            setTickets(res?.data);
          });
      } catch (error) {
        console.error(
          'Erreur lors du chargement des détails des tickets:',
          error
        );
        setTickets([]);
      } finally {
        setIsLoadingDetails(false);
      }
    },
    [user?._id]
  );

  useEffect(() => {
    if (open && button) {
      handleDetailsLoad(
        parseSessionStorageVariable('stats_assigned_ticket_status')
      );
    }
  }, [open, button, handleDetailsLoad]);

  useEffect(() => {
    sessionStorage.setItem('stats_assigned_ticket_page', page);
  }, [page]);

  const handleButton = (status) => {
    setPage(0);
    handleDetailsLoad(status);
    setButton(status);
    const isOpen = status === button && open;
    setOpen(!isOpen);
    sessionStorage.setItem(
      'stats_assigned_ticket_is_open',
      !isOpen ? user?._id : null
    );
    sessionStorage.setItem(
      'stats_assigned_ticket_status',
      !isOpen ? status : null
    );
  };

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  return (
    <>
      <TableRow key={user?._id}>
        <TableCell>
          {user?.isActive ? (
            <CheckCircleIcon size="small" color="success" />
          ) : (
            <CancelIcon size="small" color="error" />
          )}
        </TableCell>
        <TableCell>
          {user?.firstName} {user?.lastName}
        </TableCell>
        <TableCell align="right">
          <Button
            onClick={() => handleButton('NEW')}
            color="info"
            disabled={dataCount?.nbNewTickets === 0}
            variant={button === 'NEW' && open ? 'contained' : 'outlined'}
          >
            {dataCount?.nbNewTickets}
          </Button>
        </TableCell>
        <TableCell align="right">
          <Button
            onClick={() => handleButton('INPROGRESS')}
            color="info"
            disabled={dataCount?.nbInProgressTickets === 0}
            variant={button === 'INPROGRESS' && open ? 'contained' : 'outlined'}
          >
            {dataCount?.nbInProgressTickets}
          </Button>
        </TableCell>
        <TableCell align="right">
          <Button
            onClick={() => handleButton('WAITING')}
            color="info"
            disabled={dataCount?.nbWaitingTickets === 0}
            variant={button === 'WAITING' && open ? 'contained' : 'outlined'}
          >
            {dataCount?.nbWaitingTickets}
          </Button>
        </TableCell>
        <TableCell align="right">
          <Button
            onClick={() => handleButton('PROCESSED')}
            color="info"
            disabled={dataCount?.nbProcessedTickets === 0}
            variant={button === 'PROCESSED' && open ? 'contained' : 'outlined'}
          >
            {dataCount?.nbProcessedTickets}
          </Button>
        </TableCell>
        <TableCell align="right" sx={{ lineHeight: 2.5 }}>
          {user?.roles?.map((role, idx) => (
            <RolesChip key={idx} role={role} />
          ))}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                {`Détails tickets ${
                  button === 'NEW'
                    ? 'nouveau(x)'
                    : button === 'INPROGRESS'
                      ? 'en cours'
                      : button === 'WAITING'
                        ? 'en attente(s)'
                        : button === 'PROCESSED'
                          ? 'traité(s)'
                          : ''
                }`}
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Référence</TableCell>
                    <TableCell>Nº Commande</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Store</TableCell>
                    <TableCell>Catégorie</TableCell>
                    {/* <TableCell>Priorité</TableCell> */}
                    <TableCell>Messages</TableCell>
                    <TableCell>Snippet</TableCell>
                    <TableCell>Assignation</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoadingDetails ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <CircularProgress color="info" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleRows?.map((ticket, idx) => (
                      <TicketList
                        profile={profile}
                        ticket={ticket}
                        key={idx}
                        refetchUserStats={refetchUserStats}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
            {tickets ? (
              <TablePagination
                rowsPerPageOptions={[5]}
                component="div"
                count={tickets?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            ) : null}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const OperatorStatsTab = ({ sensitiveDataAllowedToUser }) => {
  const profile = useSelector((state) => state?.login?.profile);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [filterInactive, setFilterInactive] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [userRows, setUserRows] = useState([]);

  const fetchUserStats = useCallback(
    async (roleIds, inactive) => {
      setIsLoadingUsers(true);
      try {
        await AxiosConfig.create('post', `/stats/operatorTickets`)
          .call({ roleIds: roleIds, isActive: !inactive })
          .exit((res) => {
            const userServices =
              profile?.roles?.reduce((acc, role) => {
                if (role?.service?.code) {
                  acc.add(role.service.code);
                }
                return acc;
              }, new Set()) || new Set();

            const filteredUsers =
              res?.data?.filter((user) => {
                return (
                  sensitiveDataAllowedToUser ||
                  user?.roles?.some(
                    (role) =>
                      role?.service?.code && userServices.has(role.service.code)
                  )
                );
              }) || [];

            setUserRows(filteredUsers);
          });
      } catch (error) {
        console.error(
          'Erreur lors de la récupération des statistiques des utilisateurs:',
          error
        );
        setUserRows([]);
      } finally {
        setIsLoadingUsers(false);
      }
    },
    [profile, sensitiveDataAllowedToUser]
  );

  const refetchUserStats = async () => {
    await fetchUserStats(selectedRoleIds, filterInactive);
  };

  const handleFiltersChange = useCallback(
    (roleIds, inactive) => {
      setSelectedRoleIds(roleIds);
      setFilterInactive(inactive);
      fetchUserStats(roleIds, inactive);
    },
    [fetchUserStats]
  );

  return (
    <Box sx={{ width: 1250 }}>
      <TableContainer
        component={Paper}
        sx={{
          marginY: 2,
          height: '83vh',
          overflowY: 'scroll',
        }}
      >
        <Divider sx={{ margin: 2 }}>Suivi</Divider>
        <Table sx={{ minWidth: 650 }} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Nom</TableCell>
              <TableCell align="right">Nouveau</TableCell>
              <TableCell align="right">En cours</TableCell>
              <TableCell align="right">En attente</TableCell>
              <TableCell align="right">Traité</TableCell>
              <TableCell align="center">Rôles</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingUsers
              ? Array.from({ length: 5 }).map((_, index) => (
                  <UserStatsRowLazyLoading key={index} />
                ))
              : userRows.map((row) => (
                  <UserStatsRow
                    key={row._id}
                    user={row}
                    refetchUserStats={refetchUserStats}
                  />
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 'auto',
        }}
      >
        <Filters onFiltersChange={handleFiltersChange} />
      </Box>
    </Box>
  );
};

export default OperatorStatsTab;
